/**
 * AnthropicKeyContext — single source of truth for "does this user have a
 * working Anthropic key on the backend?"
 *
 * Why a context instead of useEffect-per-page:
 *   - The status is needed in 3+ places (gate, Settings, error toasts).
 *   - The gate must re-fetch when a key is saved/deleted to flip state.
 *   - Avoids redundant /user/anthropic-key calls when several pages mount
 *     in a single render.
 *
 * The actual plaintext key is NEVER held in JS state. Only metadata:
 *   { has_key, last_4, validated_at, last_validation_error }
 */
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useAuth } from './AuthContext';
import { getKeyStatus } from '../services/api';

const AnthropicKeyContext = createContext(null);

const EMPTY_STATUS = {
  has_key: false,
  last_4: null,
  validated_at: null,
  last_validation_error: '',
};

export function AnthropicKeyProvider({ children }) {
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState(EMPTY_STATUS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    // Only meaningful while logged in.
    if (!user) {
      setStatus(EMPTY_STATUS);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await getKeyStatus();
      setStatus(res.data || EMPTY_STATUS);
    } catch (e) {
      // Network errors leave the gate in "we don't know" state. We err on the
      // side of showing the gate (safer than letting them into the app and
      // failing on first Claude call) — handled in RequireAnthropicKey.
      setError(e.friendlyMessage || 'Could not check key status.');
      setStatus(EMPTY_STATUS);
    }
    setLoading(false);
  }, [user]);

  // Fetch on login, reset on logout.
  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const value = {
    ...status,
    loading,
    error,
    refresh,
  };

  return (
    <AnthropicKeyContext.Provider value={value}>
      {children}
    </AnthropicKeyContext.Provider>
  );
}

export const useAnthropicKey = () => useContext(AnthropicKeyContext);
